import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Trash2, ArrowRight, Building2, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/buildings`;

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '', location: '', floors_count: 0, total_area: 0, steel_factor: 120
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      // Handle both array response and {items: [...]} response
      const projectsData = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setProjects(projectsData);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name) {
      toast.error('الرجاء إدخال اسم المشروع');
      return;
    }
    try {
      const response = await axios.post(`${API}/projects`, {
        ...newProject,
        unit_templates: [] // Start with empty templates
      });
      toast.success('تم إنشاء المشروع بنجاح');
      setShowAddForm(false);
      setNewProject({ name: '', location: '', floors_count: 0, total_area: 0, steel_factor: 120 });
      navigate(`/buildings/projects/${response.data.id}`);
    } catch (error) {
      toast.error('خطأ في إنشاء المشروع');
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success('تم الحذف');
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحذف');
    }
  };

  // Calculate totals from embedded templates
  const getTotalUnits = (templates) => templates?.reduce((sum, t) => sum + (t.count || 0), 0) || 0;

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-600" />
            المشاريع
          </h1>
          <p className="text-sm text-gray-500 mt-1">{projects.length} مشروع مسجل</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddForm(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
            <Plus className="w-4 h-4" />
            مشروع جديد
          </Button>
          <Button onClick={() => navigate('/buildings')} variant="outline" className="gap-2">
            <ArrowRight className="w-4 h-4" />
            العودة
          </Button>
        </div>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card className="shadow-md border-0">
          <CardContent className="p-8 text-center">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">لا توجد مشاريع بعد</p>
            <Button onClick={() => setShowAddForm(true)} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 ml-2" />
              إنشاء مشروع جديد
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Card key={project.id} className="shadow-md border-0 hover:shadow-lg transition-shadow">
              <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 rounded-t-xl">
                <CardTitle className="text-lg">{project.name}</CardTitle>
                {project.location && <p className="text-sm text-amber-100">{project.location}</p>}
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div className="bg-gray-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-500">الشقق</p>
                    <p className="font-bold text-lg">{getTotalUnits(project.unit_templates)}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-500">النماذج</p>
                    <p className="font-bold text-lg">{project.unit_templates?.length || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-500">المساحة</p>
                    <p className="font-bold text-lg">{project.total_area?.toLocaleString()} م²</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-500">الحالة</p>
                    <p className={`font-bold text-lg ${
                      project.status === 'مكتمل' ? 'text-green-600' :
                      project.status === 'متوقف' ? 'text-red-600' : 'text-blue-600'
                    }`}>{project.status}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate(`/buildings/projects/${project.id}`)}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 gap-1 h-9"
                  >
                    <Eye className="w-4 h-4" />
                    تفاصيل
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDeleteProject(project.id)}
                    className="text-red-500 border-red-200 hover:bg-red-50 h-9 w-9 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Project Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="bg-amber-600 text-white p-4 rounded-t-xl">
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                إنشاء مشروع جديد
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">اسم المشروع *</Label>
                  <Input
                    placeholder="عمارة الرياض"
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">الموقع</Label>
                  <Input
                    placeholder="حي النرجس"
                    value={newProject.location}
                    onChange={(e) => setNewProject({...newProject, location: e.target.value})}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">عدد الأدوار</Label>
                  <Input
                    type="number"
                    value={newProject.floors_count}
                    onChange={(e) => setNewProject({...newProject, floors_count: parseInt(e.target.value) || 0})}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">المساحة الإجمالية (م²)</Label>
                  <Input
                    type="number"
                    value={newProject.total_area}
                    onChange={(e) => setNewProject({...newProject, total_area: parseFloat(e.target.value) || 0})}
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">معامل التسليح (كجم/م²)</Label>
                  <Input
                    type="number"
                    value={newProject.steel_factor}
                    onChange={(e) => setNewProject({...newProject, steel_factor: parseFloat(e.target.value) || 120})}
                    className="h-9"
                  />
                  <p className="text-xs text-gray-400 mt-1">القيمة الافتراضية: 120 كجم/م²</p>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                💡 بعد إنشاء المشروع، يمكنك إضافة نماذج الشقق الخاصة به من صفحة التفاصيل
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleCreateProject} className="flex-1 bg-amber-600 hover:bg-amber-700">
                  إنشاء المشروع
                </Button>
                <Button onClick={() => setShowAddForm(false)} variant="outline" className="flex-1">
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
